import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, ArrowRight, MailCheck } from "lucide-react";

export const LearnMoreModal = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/leads/learn-more", form);
      setSent(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  const cls = "w-full bg-white border border-[#E7E5E4] rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal";
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="learn-more-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} data-testid="learn-more-close" className="absolute top-4 right-4 text-[#78716C] hover:text-navy"><X className="w-5 h-5" /></button>
        {sent ? (
          <div className="text-center py-4" data-testid="learn-more-success">
            <MailCheck className="w-12 h-12 text-teal mx-auto" />
            <h3 className="mt-4 font-heading text-2xl text-navy">Check your inbox!</h3>
            <p className="mt-3 text-sm text-[#57534E] leading-relaxed">
              We've emailed you everything you need to know about the program — what's included,
              who it's for, and everything included in your membership.
            </p>
            <button onClick={onClose} className="mt-6 bg-navy text-white rounded-full px-7 py-2.5 text-sm font-medium hover:bg-navy-dark">Close</button>
          </div>
        ) : (
          <>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-teal">Enrolment is open now</span>
            <h3 className="mt-2 font-heading text-2xl text-navy">Want to learn more?</h3>
            <p className="mt-2 text-sm text-[#57534E] leading-relaxed">
              Leave your details and we'll send you a full overview of the program — plus first access
              to registration spots when enrolment opens.
            </p>
            <form onSubmit={submit} className="mt-5 space-y-4">
              <input data-testid="learn-more-name" required placeholder="First name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} className={cls} />
              <input data-testid="learn-more-email" type="email" required placeholder="Email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} className={cls} />
              <button data-testid="learn-more-submit" type="submit" disabled={busy}
                className="w-full bg-teal text-white hover:bg-teal-dark transition-colors rounded-full py-3 font-medium flex items-center justify-center gap-2 disabled:opacity-60">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Send me the details
              </button>
              <p className="text-xs text-[#78716C] text-center leading-relaxed" data-testid="learn-more-reassurance">
                No spam, we promise — just the programme details and the occasional launch update.
                We'll never share your details, and you can unsubscribe at any time.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
};
